import 'pages/BuySubscription/buy-subscription.css';
import {Subscription} from 'pages/Join/Join.types';
import React, {BaseSyntheticEvent, useEffect, useRef, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import Page from 'src/layout/Page';
import {TSubscriptionType} from 'src/pages/BuySubscription/BuySubscription.types';
import {useTranslation} from 'react-i18next';
import Calendar from 'react-calendar';
import {Value} from 'src/shared/types';
import {format} from 'date-fns';
import {DEFAULT_HEADERS} from 'src/shared/constants';
import useChildren from 'src/hooks/useChildren';

const BuySubscription: React.FC = (): React.ReactElement => {
    const {t, i18n}                     = useTranslation();
    const {search, pathname}            = useLocation();
    const searchParams                  = new URLSearchParams(search);
    const subscriptionId: string | null = searchParams.get('subscriptionId'),
          isOnline                      = searchParams.get('isOnline') === 'true';

    const [subscriptions, setSubscriptions]                   = useState<Subscription[]>([]);
    const [filteredSubscriptions, setFilteredSubscriptions]   = useState<Subscription[]>([]);
    const [subscriptionsType, setSubscriptionsType]           = useState<TSubscriptionType>(isOnline ? 'online' : 'offline');
    const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<number | null>(subscriptionId ? parseInt(subscriptionId) : null);
    const [selectedSubscription, setSelectedSubscription]     = useState<Subscription | null>(null);
    const [selectedChild, setSelectedChild]                   = useState<any | null>('');
    const [isShowingCalendar, setIsShowingCalendar]           = useState<boolean>(false);
    const [calendarValue, setCalendarValue]                   = useState<Value>(new Date());
    const [calendarLanguage, setCalendarLanguage]             = useState(i18n.language);
	const [startDate, setStartDate] = useState<string>(format(new Date(), 'yyyy-MM-dd'));
	const [promoCode, setPromoCode] = useState<string>('');
	const { children } = useChildren();
	const { getToken } = useAuth();
	const [bundle, setBundle] = useState<any>(null);
	const [periodEventError, setPeriodEventError] = useState<boolean>(true);
	const [checkedBundles, setCheckedBundles] = useState<number[]>([]);

	const navigate = useNavigate();
	const { isAuthenticated } = useAuth();
	const checkAuth = () => {
		if (!isAuthenticated())
			if (i18n.language !== 'en') {
				navigate('/התחברות');
			} else {
				navigate('/en/login');
			}
	};

	const formRef = useRef() as React.MutableRefObject<HTMLFormElement>;
	useEffect(() => {
		const getData = async () => {
			const data = await fetch(`${import.meta.env.VITE_API_URL}/subscriptions`, {
				method: 'GET',
			});
			const jsonData = await data.json();
			setSubscriptions(jsonData);
			setFilteredSubscriptions(
				jsonData.filter((subscription: Subscription) => subscription.isOnline === isOnline),
			);
		};

		getData().then();
	}, []);

	useEffect(() => {
		setCalendarLanguage(i18n.language);
	}, [i18n.language]);

	useEffect(() => {
		if (selectedSubscription?.bundleId) {
			const token = getToken();
			const getBundle = async () => {
				const data = await fetch(`${import.meta.env.VITE_API_URL}/lessons-bundle/${selectedSubscription?.bundleId}`, {
					method: 'GET',
					headers: {
						...DEFAULT_HEADERS,
						Authorization: `Bearer ${token}`,
					},
				});
				const jsonData = await data.json();
				setBundle(jsonData);
			};
			getBundle().then();
		}
	}, [selectedSubscription]);

	useEffect(() => {
		if (subscriptionId && !selectedSubscription) {
			setSelectedSubscription(subscriptions.find((s: Subscription) => s.id === parseInt(subscriptionId, 10)) || null);
		}
	}, [subscriptions]);

	const buySubscription = async () => {
		checkAuth();
		if (selectedSubscriptionId !== null && startDate !== '') {
            const request: any = {
                subscriptionId: selectedSubscriptionId,
                startDate,
            };
            if (selectedSubscription?.bundleId) {
                request.childId   = selectedChild.id;
                request.startDate = bundle.startDate;
            }
            if (selectedSubscription?.camp) {
                request.childId   = selectedChild.id;
                request.bundleIds = [...checkedBundles];
            }
            const token = getToken();

            if (selectedSubscription?.camp !== null) {
                const priceForPeriod = selectedSubscription?.camp.prices.filter((price: any) => price.periodCount === request.bundleIds.length).shift();
                if (priceForPeriod.stripePaymentLink) {
                    window.location = priceForPeriod.stripePaymentLink as any;
                }
                return;
            }

            if (selectedSubscription?.stripePaymentLink !== null) {
                window.location = selectedSubscription.stripePaymentLink as any;
                return;
            }

            const buyResponse = await fetch(`${import.meta.env.VITE_API_URL}/purchased-subscriptions`, {
                method : 'POST',
                headers: {
                    ...DEFAULT_HEADERS,
                    Authorization: `Bearer ${token}`,
                },
                body   : JSON.stringify(request),
            });
            if (buyResponse.status === 200) {
                const {link}    = await buyResponse.json();
                window.location = link;
            }

        }
	};

	const handleStep = (e: BaseSyntheticEvent) => {
		e.preventDefault();
		buySubscription().then();
	};

	useEffect(() => {
		const params = new URLSearchParams(search);
		params.set('isOnline', (subscriptionsType === 'online').toString());
		if (selectedSubscriptionId !== null) {
			params.set('subscriptionId', selectedSubscriptionId.toString());
		} else {
			params.delete('subscriptionId');
		}
		navigate(`${pathname}?${params.toString()}`);
	}, [subscriptionsType, selectedSubscriptionId]);

	const handleType = (e: any, sType: TSubscriptionType) => {
		e.preventDefault();

		if (sType !== subscriptionsType) {
			setSubscriptionsType(sType);
			switch (sType) {
				case 'online':
					setFilteredSubscriptions(
						subscriptions.filter((subscription) => subscription.isOnline),
					);
					break;
				case 'offline':
					setFilteredSubscriptions(
						subscriptions.filter((subscription) => !subscription.isOnline),
					);
					break;
				default:
					setFilteredSubscriptions(
						subscriptions.filter((subscription) => subscription.isOnline),
					);
					break;
			}
			setSelectedSubscriptionId(null);
			setSelectedSubscription(null);
			setSelectedChild('');
		}
	};

	const handleSelectedSubscription = (e: BaseSyntheticEvent, subscription: Subscription) => {
		e.preventDefault();
		setSelectedSubscriptionId(subscription.id);
		setSelectedSubscription(subscription);
		setCheckedBundles([]);
		setFilteredSubscriptions(filteredSubscriptions.map(
			(subscription) => {
				subscription.price = 0;
				return subscription;
			}
		));
	};

	const onChange = (value: any) => {
		setCalendarValue(value);
		setStartDate(format(value as Date, 'yyyy-MM-dd'));
		setIsShowingCalendar(false);
	};

	const handlePromoCode = (e: BaseSyntheticEvent) => {
		setPromoCode(e.target.value);
	};

	const getSubscriptionName = (subscription: Subscription): string => {
		if (i18n.language !== 'en') {
			return `${subscription.nameHebrew || ''}${subscription.nameHebrew && subscription.price > 0 ? ': ' : ''}`;
		}
		return `${subscription.name || ''}${subscription.name && subscription.price > 0 ? ': ' : ''}`;
	};

	const getSubscriptionPrice =  (subscription: Subscription): string => {
		if (subscription.price > 0) {
			return `${subscription.price} ${t('pages.buySubscription.euros')}`;
		}
		return '';
	}

	const handleSelectChild = (e: BaseSyntheticEvent) => {
		setSelectedChild(children?.find((c: any) => c.id === parseInt(e.target.value, 10)));
	};

	useEffect(() => {
		if (selectedSubscription && selectedSubscription.camp) {
			setPeriodEventError(
				selectedSubscription?.camp?.prices
					?.filter((price: any) => price.periodCount === checkedBundles.length).length === 0 || checkedBundles.length === 0
			);

			const price = selectedSubscription.camp.prices.find((price: any) => price.periodCount === checkedBundles.length);
				setFilteredSubscriptions(filteredSubscriptions.map(
					(subscription) => {
						if (subscription.id === selectedSubscription.id) {
							subscription.price = price?.amount || 0;
						}
						return subscription;
					}
				));
				return;
		}
		setPeriodEventError(false);
	}, [selectedSubscriptionId, selectedSubscription, checkedBundles.length]);

	const handleCheckPeriod = (e: BaseSyntheticEvent, p: any) => {
		if (e.target.checked) {
			setCheckedBundles([...checkedBundles, p.bundleId as number]);
		} else {
			setCheckedBundles(checkedBundles.filter((bundleId: number) => bundleId !== p.bundleId));
		}
	};

	const getCounts = () => {
		const periods = selectedSubscription?.camp?.prices.map((p: any) => p.periodCount).sort();
		return periods.join(', ');
	}

	return (
		<Page pageClass={'subscribe-page'}>
			<div className="subscribe-steps">
				<div className="subscribe-step-description">
					<div className="subscribe-step-title">
						{t(`pages.buySubscription.steps.1.stepTitle`)}
					</div>
				</div>
				<form className="subscribe-step-form" ref={formRef}>
					<div className="subscribe-form">
						<div className="subscription-selection">
							<div className="button-container start">
								<button
									className={`${subscriptionsType === 'offline' ? 'active' : ''} scroll-to-button`}
									onClick={(e) => handleType(e, 'offline')}
								>
									{t('pages.buySubscription.frontal')}
								</button>
								<button
									className={`${subscriptionsType === 'online' ? 'active' : ''} scroll-to-button`}
									onClick={(e) => handleType(e, 'online')}
								>
									{t('pages.buySubscription.digital')}
								</button>
							</div>
							<div className="purchased-subscriptions-container">
								{filteredSubscriptions.map((subscription) => (
									<div
										className="subscription-to-select"
										key={subscription.id}
									>
										<div className={'subscription-with-price'}>
											{getSubscriptionName(subscription)}{getSubscriptionPrice(subscription)}
										</div>
										<div className="select-subscription-button">
											<button
												className={`scroll-to-button select-subscription ${subscription.id === selectedSubscriptionId ? 'active' : ''}`}
												onClick={(e) => handleSelectedSubscription(e, subscription)
												}
											>
												{t('pages.buySubscription.selectSubscription')}
											</button>
										</div>
									</div>
								))}
							</div>
							{!selectedSubscription ? (
									<div className="calendar-container">
										<label htmlFor={'startDate'}>
											{t('pages.buySubscription.startDate')}
										</label>
										<div className={'calendar-wrapper'}>
											{isShowingCalendar && <div className="calendar">
												<Calendar
													view={'month'}
													onChange={(value: any) => onChange(value)}
													value={calendarValue}
													calendarType={'hebrew'}
													locale={calendarLanguage}
													minDate={new Date()}
												/>
											</div>}
											<input
												id={'startDate'}
												name={'startDate'}
												type="text"
												readOnly={true}
												onClick={() => setIsShowingCalendar(!isShowingCalendar)}
												value={startDate}
											/>
										</div>
									</div>
								) :
								(
									<div className={'select-child-wrapper'}>
										<label htmlFor="selectedChild">
											{t('pages.buySubscription.selectChild')}
										</label>
										<select name="selectedChild" id="selectedChild" onChange={handleSelectChild} value={selectedChild}>
											<option value="" disabled>
												Please select
											</option>
											{children?.map((c: any) => <option value={c.id}
																												 key={c.id}>{`${c.firstname} ${c.lastname}`}</option>)}
										</select>
									</div>
								)
							}
							{
								selectedSubscription && selectedSubscription.camp !== null ? (
									<>
										<div className={'event-periods-wrapper'}>
											<label htmlFor="selectedChild">
												{t('pages.buySubscription.selectPeriods')}
											</label>
											<div className="periods">
												{selectedSubscription.camp.periods.map((p: any, index: number) => {
													return <label htmlFor={`s-period-${index}`} key={index}>
														<input type="checkbox" name={`s-period-${index}`}
															   id={`s-period-${index}`}
															   checked={checkedBundles.includes(p.bundleId)}
															   onChange={(e: BaseSyntheticEvent) => handleCheckPeriod(e, p)}/>
														{`${p.periodStartDate} - ${p.periodEndDate}`} ({p.periodStartTime.slice(0, -3)} - {p.periodEndTime.slice(0, -3)})
													</label>;
												})}
											</div>
											{periodEventError && <div className={'error'}>
												{t('pages.buySubscription.eventPeriodError')}{getCounts()}
											</div>}
										</div>
									</>
								) : null
							}
							<div className="coupon-code-container">
								<label htmlFor={'startDate'}>
									{t('pages.buySubscription.promoCode')}
								</label>
								<input
									id={'promoCode'}
									name={'promoCode'}
									type="text"
									readOnly={false}
									value={promoCode}
									onChange={handlePromoCode}
								/>
							</div>
							<div className="button-container next-step">
								<button
									className={'scroll-to-button'}
									onClick={handleStep}
									disabled={periodEventError || !selectedChild?.id}
								>
									{t('common.continue')}
								</button>
							</div>
						</div>
					</div>
				</form>
			</div>
		</Page>
	);
};

export default BuySubscription;
